import { IndeterminateCheckbox } from "@bcc/components/ui/checkbox";
import { cn } from "@bcc/lib/utils";
import { flexRender, type Header, type RowSelectionState, type Table } from "@tanstack/react-table";
import { ChevronDown, ChevronsUpDown, ChevronUp } from "lucide-react";
import { DataTableSelectAllToggle } from "./data-table-select-all-toggle";
import type { DataTableRow } from "./data-table.types";
import {
  setHeaderColumnTestId,
  setHeaderRowTestId,
  setHeaderSortButtonTestId,
  setHeaderTestId,
  setSelectAllCheckboxTestId,
  setSelectAllRowTestId,
} from "./data-table.utils";
import { useSelectAll } from "./use-select-all";

interface DataTableHeaderProps<T extends object> {
  table: Table<DataTableRow<T>>;
  selectable?: boolean;
  showExpanderColumn?: boolean;
  maxSelectableRows?: number;
  setRowSelection?: (selection: RowSelectionState) => void;
  /** When set, the select-all checkbox is replaced by a labelled DataTableSelectAllToggle button. */
  selectAllLabel?: string;
}

/**
 * Sort indicator component
 */
function SortIndicator({ direction }: Readonly<{ direction: false | "asc" | "desc" }>) {
  if (direction === "asc") {
    return <ChevronUp className="ml-1 size-4" />;
  }
  if (direction === "desc") {
    return <ChevronDown className="ml-1 size-4" />;
  }
  return <ChevronsUpDown className="ml-1 size-4 opacity-50" />;
}

/**
 * Individual header cell
 */
function HeaderCell<T extends object>({ header }: Readonly<{ header: Header<DataTableRow<T>, unknown> }>) {
  const canSort = header.column.getCanSort();
  const isSorted = header.column.getIsSorted();

  if (header.isPlaceholder) {
    return null;
  }

  const content = flexRender(header.column.columnDef.header, header.getContext());

  if (!canSort) {
    return <div className="flex items-center">{content}</div>;
  }

  return (
    <button
      type="button"
      data-testid={setHeaderSortButtonTestId(header.id)}
      className="flex items-center hover:text-foreground transition-colors -ml-2 px-2 py-1 rounded-md hover:bg-muted/50"
      onClick={header.column.getToggleSortingHandler()}
    >
      {content}
      <SortIndicator direction={isSorted} />
    </button>
  );
}

/**
 * DataTableHeader component
 *
 * Renders the table header with sortable columns and optional select-all checkbox.
 */
export function DataTableHeader<T extends object>({
  table,
  selectable = false,
  showExpanderColumn = false,
  maxSelectableRows,
  setRowSelection,
  selectAllLabel,
}: Readonly<DataTableHeaderProps<T>>) {
  const { isAllSelected, isSomeSelected, hasSelectableRows, toggleSelectAll } = useSelectAll({
    table,
    maxSelectableRows,
    setRowSelection,
  });

  return (
    <thead data-testid={setHeaderTestId()} className="border-b border-border bg-muted/30 sticky top-0 z-10">
      {selectable && selectAllLabel && (
        // Own full-width row: button text is too wide for the 50px selection column.
        <tr data-testid={setSelectAllRowTestId()}>
          <th colSpan={table.getVisibleLeafColumns().length + 1 + (showExpanderColumn ? 1 : 0)} className="px-4 py-2 text-left">
            <DataTableSelectAllToggle
              table={table}
              label={selectAllLabel}
              maxSelectableRows={maxSelectableRows}
              setRowSelection={setRowSelection}
            />
          </th>
        </tr>
      )}
      {table.getHeaderGroups().map((headerGroup) => (
        <tr key={headerGroup.id} data-testid={setHeaderRowTestId()}>
          {showExpanderColumn && <th className="w-[50px] px-4 py-3" />}
          {selectable && (
            <th className="w-[50px] px-4 py-3">
              {!selectAllLabel && (
                <IndeterminateCheckbox
                  testId={setSelectAllCheckboxTestId()}
                  checked={isAllSelected}
                  indeterminate={isSomeSelected}
                  disabled={!hasSelectableRows}
                  onChange={toggleSelectAll}
                  aria-label="Select all rows"
                />
              )}
            </th>
          )}
          {headerGroup.headers.map((header) => (
            <th
              key={header.id}
              data-testid={setHeaderColumnTestId(header.id)}
              className={cn(
                "px-4 py-3 text-left text-xs font-semibold text-muted-foreground  tracking-wider",
                header.column.getCanSort() && "cursor-pointer select-none"
              )}
              style={{
                width: header.getSize() !== 150 ? header.getSize() : undefined,
              }}
            >
              <HeaderCell header={header} />
            </th>
          ))}
        </tr>
      ))}
    </thead>
  );
}
